import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import { Summary } from "../types";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";

interface VerificationItemProps {
  label: string;
  value: string;
  status?: Summary | string;
  poBox?: string;
}

const VerificationBadge = ({ status }: { status: Summary | string }) => {
  switch (status) {
    case Summary.PartialMatch:
      return (
        <Badge
          size={BadgeSize.BASE}
          variant={BadgeVariant.WARNING}
          className="items-center"
          leftElement={
            <InfoIcon className="!text-warning-500 !stroke-warning-500 h-4 w-4" />
          }
        >
          Partial match
        </Badge>
      );
    case Summary.Match:
      return (
        <Badge
          size={BadgeSize.BASE}
          variant={BadgeVariant.SUCCESS}
          className="items-center"
          leftElement={<SuccessIcon className="text-success-500 h-4 w-4" />}
        >
          Match
        </Badge>
      );
    case Summary.NoMatch:
    case Summary.NoData:
      return (
        <Badge size={BadgeSize.BASE} variant={BadgeVariant.BASE}>
          No data
        </Badge>
      );
    case Summary.NoInput:
      return (
        <Badge size={BadgeSize.BASE} variant={BadgeVariant.BASE}>
          No input
        </Badge>
      );
  }
};

export const VerificationItem = ({
  label,
  value,
  status,
  poBox
}: VerificationItemProps) => {
  return (
    <div className="py-4 flex gap-2 border-b border-alpha-40 items-center last-of-type:border-transparent">
      <div className="max-w-[180px] w-full">
        <p className="font-bold mb-2">{label}</p>
        <p className="text-sm font-medium text-content-1000 truncate capitalize">
          {value}
        </p>
      </div>

      <div>
        {poBox ? (
          <>
            <div className="text-sm font-medium text-content-300 mb-2">
              P.O. box
            </div>
            <div>{poBox}</div>
          </>
        ) : (
          <Tooltip>
            <TooltipTrigger>
              <VerificationBadge status={status || ""} />
            </TooltipTrigger>
            {status === Summary.Match ? (
              <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
                <>
                  <p className="font-bold mb-2">
                    Full match on proprietary source
                  </p>
                  <p>The input was a perfect match</p>
                </>
              </TooltipContent>
            ) : status === Summary.NoMatch ? (
              <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
                <>
                  <p className="font-bold mb-2">
                    No match on proprietary source
                  </p>
                  <p>The input data did not match</p>
                </>
              </TooltipContent>
            ) : status === Summary.PartialMatch ? (
              <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
                <>
                  <p className="font-bold mb-2">
                    Partial match on proprietary source
                  </p>
                  <p>The input was a partial match</p>
                </>
              </TooltipContent>
            ) : status === Summary.NoData ? (
              <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
                <>
                  <p className="font-bold mb-2">
                    No data on proprietary source
                  </p>
                  <p>No data was found</p>
                </>
              </TooltipContent>
            ) : status === Summary.NoInput ? (
              <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
                <>
                  <p className="font-bold mb-2">No input</p>
                  <p>No information provided for this field by the end user</p>
                </>
              </TooltipContent>
            ) : null}
          </Tooltip>
        )}
      </div>
    </div>
  );
};
